import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  LinearProgress,
  Badge,
  Tab,
  Tabs,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Link,
  useMediaQuery,
  useTheme
} from "@mui/material";
import bkstApi from "../../common/api";
import { Link as RouterLink } from "react-router-dom";
import ConfigContext from "../../common/context/ConfigContext";
import { AppStateContext } from "../../common/context/AppStateContext";
import ProductSummary from "../component/ProductSummary";
import { anyWordStartsWith, capitalize, checkItemAvailabilityByDate, checkStoreAvailabilityByDate, getItemUrl } from "../../common/util";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CustomItemSummaryTile from "../component/CustomItemSummaryTile";
import moment from "moment";
import FulfillmentOptionsPreCheck from "../../cart/component/FulfillmentOptionsPreCheck";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import NotInterestedOutlinedIcon from "@mui/icons-material/NotInterestedOutlined";

export default function StandardCatalog() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const siteConfig = useContext(ConfigContext);
  const { cart, resetCartState } = useContext(AppStateContext);

  const [categoryMap, setCategoryMap] = useState("");

  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [date, setDate] = useState(() => {
    const d = sessionStorage.getItem("cart.date");
    return d || "";
  });

  const [category, setCategory] = useState("");
  const [products, setProducts] = useState([]);
  const [storeLocation, setStoreLocation] = useState("");

  const [loading, setLoading] = useState(true);
  const [showUnavailableList, setShowUnavailableList] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const handleDateChange = (newDate) => {
    setDate(newDate);
    if (newDate) {
      sessionStorage.setItem("cart.date", newDate);
    } else {
      sessionStorage.removeItem("cart.date");
    }
    resetCartState();
  };

  useEffect(() => {
    bkstApi(`/site/${siteConfig.id}/product/collection/shop`)
      .then((res) => {
        let sortedByRank = res.data.products.sort((o1, o2) => {
          let rslt = (+o2.rank || 0) - (+o1.rank || 0);
          if (rslt === 0) {
            rslt = o1.title > o2.title ? 1 : -1;
          }
          return rslt;
        });
        setProducts(sortedByRank);

        let map = {};
        for (const p of sortedByRank) {
          const cats = p.category || [p.type.toLowerCase()];
          for (let cat of cats) {
            cat = cat.toLowerCase();
            if (cat !== "rosh hashanah" && cat !== "yom kippur") {
              const arr = map[cat] || [];
              arr.push(p);
              map[cat] = arr;
            }
          }
        }
        setCategoryMap(map);
        setStoreLocation(res.data.storeLocation);

        setLoading(false);

        setFiltered(sortedByRank);

        if (window.location.hash) {
          const category = window.location.hash.substring(1).replaceAll("-", " ");
          console.log("window.location.hash", window.location.hash, category);

          setCategory(category);

          setTimeout(() => {
            scrollToCategory(category);
          }, 500);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }, []);

  const handleQueryChange = (q) => {
    setQuery(q);
    setCategory("");

    scrollToCategory("product-list");

    // add a delay to prevent shifting
    setTimeout(() => {
      setFiltered(products.filter((o) => anyWordStartsWith(o.title, q) || anyWordStartsWith(o.category ? o.category.join(", ") : o.type, query)));
    }, 500);
  };

  const scrollToCategory = (cat) => {
    const el = document.getElementById(cat);
    if (el) {
      el.scrollIntoView();
    }
  };

  const getRank = (cat, arr) => {
    cat = cat.toLowerCase();
    if (cat.includes("featured")) return 10000;
    if (cat.includes("thanksgiving")) return 8000;
    if (cat.includes("halloween")) return 9000;

    const rank = arr.reduce((acc, o) => +acc + +o.rank, 0);
    const itemsWithEndDate = arr.filter((o) => o.rules?.date?.max);

    if (itemsWithEndDate.length > 0 && itemsWithEndDate.length / arr.length > 0.4) {
      // if half of items in category have end date, sort by end date
      return 8000;
    } else if (rank > arr.length) {
      // sort by bestsellers
      return rank;
    } else {
      // sort by number of items in category
      return arr.length; // all other strings
    }
  };

  const sortCategories = ([k1, v1], [k2, v2]) => {
    const rankA = getRank(k1, v1);
    const rankB = getRank(k2, v2);

    if (rankA === rankB) {
      return k1.localeCompare(k2);
    } else {
      return rankB - rankA;
    }
  };

  const isItemAvailable = (product) => {
    if (!date) return true;
    return (
      checkStoreAvailabilityByDate(siteConfig.id, siteConfig.storeLocations[0], date) &&
      checkItemAvailabilityByDate(siteConfig.id, [product], date, siteConfig.storeLocations[0])
    );
  };

  const separateByAvailability = (products) => {
    if (!date) return { available: products, unavailable: [] };
    const available = products.filter((p) => isItemAvailable(p));
    const unavailable = products.filter((p) => !isItemAvailable(p));
    return { available, unavailable };
  };

  const aggregatedUnavailable = useMemo(() => {
    if (!date) return [];
    const source = filtered.length < products.length ? filtered : products;
    return source.filter((product) => !isItemAvailable(product));
  }, [date, filtered, products]);

  useEffect(() => {
    setShowUnavailableList(false);
  }, [date, query]);

  useEffect(() => {
    if (query) {
      setShowSearch(true);
    }
  }, [query]);

  /** Using filtered instead of query since results are not rendered before this is executed */
  useEffect(() => {
    if (filtered.length === products.length) {
      if (category) {
        scrollToCategory(category);
      } else {
        scrollToCategory("product-list");
      }
    }
  }, [filtered]);

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Container maxWidth="lg" disableGutters={isMobile} sx={{ paddingTop: "2rem" }}>
        <Box
          sx={{
            backgroundColor: "#F7F7F7",
            position: "-webkit-sticky",
            position: "sticky",
            top: "0",
            marginBottom: "2rem",
            zIndex: 10,
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-20px",
              left: 0,
              right: 0,
              height: "20px",
              background: "linear-gradient(to bottom, rgba(247, 247, 247, 1), rgba(247, 247, 247, 0))",
              pointerEvents: "none",
              zIndex: 1
            }
          }}
        >
          <Box px={1} pt={1}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={10}>
                <Box mb={1} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Typography variant="h4" sx={{ whiteSpace: "nowrap" }}>
                    Online Shop
                  </Typography>
                  <FulfillmentOptionsPreCheck
                    date={date}
                    onChange={(o) => {
                      handleDateChange(o.date);
                    }}
                  >
                    <Link sx={{ cursor: "pointer" }}>{date ? moment(date).format("ddd, MMM D, YYYY") : "Pick a Date"}</Link>
                  </FulfillmentOptionsPreCheck>
                </Box>
              </Grid>
              <Grid item xs={2} textAlign="right">
                <Box sx={{ flexGrow: 0, minWidth: "2rem" }}>
                  <IconButton component={RouterLink} to={cart.quantity > 0 ? `/cart/${cart.id}` : `/shop`}>
                    <Badge badgeContent={cart.quantity} color="primary">
                      <ShoppingCartIcon />
                    </Badge>
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", borderTop: "2px solid #DDD", borderBottom: "2px solid #DDD" }}>
            {showSearch ? (
              <TextField
                id="query"
                value={query}
                label=""
                name="query"
                variant="outlined"
                autoFocus
                onChange={(e) => {
                  handleQueryChange(e.target.value);
                }}
                placeholder="Filter items by name or category"
                InputProps={{
                  startAdornment: (
                    <InputAdornment sx={{ paddingRight: "10px" }}>
                      <FilterListIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment>
                      <IconButton
                        size="small"
                        onClick={() => {
                            handleQueryChange("");
                            setShowSearch(false);
                        }}
                      >
                        <CloseOutlinedIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  flex: 1
                }}
              />
            ) : (
              <IconButton onClick={() => setShowSearch(true)}>
                <SearchIcon />
              </IconButton>
            )}
            <Tabs
              value={category}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                flex: showSearch ? 0 : 1,
                "& .MuiTabs-scrollButtons": {
                  width: "32px",
                  "&:first-of-type": {
                    paddingRight: "4px"
                  }
                },
                "& .MuiTab-root": {
                  "&:first-of-type": {
                    marginLeft: "-8px"
                  }
                }
              }}
            >
            {Object.entries(categoryMap)
              .sort(sortCategories)
              .map(([cat, arr], idx) => {
                const { available } = separateByAvailability(arr);
                if (available.length === 0) {
                  return null;
                }
                return (
                  <Tab
                    key={idx}
                    label={cat}
                    value={cat}
                    onClick={() => {
                      setCategory(cat);
                      if (query) {
                        setQuery("");
                        setFiltered(products);
                        setShowSearch(false);
                      } else {
                        // category list is already rendered, simply scroll to category
                        scrollToCategory(cat);
                      }
                    }}
                    sx={{ minWidth: "50px", padding: "12px" }}
                  />
                );
              })}
            </Tabs>
          </Box>
        </Box>

        <Box id="product-list" sx={{ scrollMarginTop: "150px", paddingBottom: "200px" }}>
          {filtered.length < products.length
            ? (() => {
                const { available } = separateByAvailability(filtered);
                return (
                  <Grid container spacing={2}>
                    {available.map((o, index) => {
                      return (
                        <Grid item xs={12} sm={6}>
                          <FulfillmentOptionsPreCheck
                            product={o}
                            date={date}
                            onChange={(obj) => {
                              handleDateChange(obj.date);
                            }}
                            target={getItemUrl(o)}
                          >
                            <ProductSummary key={`rslt-${index}`} product={o} query={query} isAvailable={true} />
                          </FulfillmentOptionsPreCheck>
                        </Grid>
                      );
                    })}
                  </Grid>
                );
              })()
            : Object.entries(categoryMap)
                .sort(sortCategories)
                .map(([key]) => {
                  const { available } = separateByAvailability(categoryMap[key]);
                  if (available.length === 0) {
                    return null;
                  }
                  return (
                    <Box mb={4} id={key} sx={{ scrollMarginTop: "150px" }}>
                      <Box pl={1} mb={2} textAlign={"left"}>
                        <Typography variant="h4" color="textSecondary" sx={{ textDecoration: "underline" }}>
                          {capitalize(key)}
                        </Typography>
                      </Box>
                      <Grid container spacing={3}>
                        {available.map((product, index) => {
                          return (
                            <Grid item xs={12} sm={6}>
                              <FulfillmentOptionsPreCheck
                                product={product}
                                date={date}
                                onChange={(obj) => {
                                  handleDateChange(obj.date);
                                }}
                                target={getItemUrl(product)}
                              >
                                <ProductSummary key={`rslt-${index}`} product={product} query={query} isAvailable={true} />
                              </FulfillmentOptionsPreCheck>
                            </Grid>
                          );
                        })}
                        {!(siteConfig.id === "Z3bGmikh3cWqw3HYKGIA" || siteConfig.id === "ZOsiHlROlfEcAO7PGGCK") &&
                          (key.toLowerCase() === "cake" || key.toLowerCase() === "cakes" || key.toLowerCase().includes(" cake")) && (
                            <CustomItemSummaryTile></CustomItemSummaryTile>
                          )}
                      </Grid>
                    </Box>
                  );
                })}
          {date && aggregatedUnavailable.length > 0 && (
            <Box id="unavailable-items" sx={{ marginTop: 6 }}>
              <Box mb={2} textAlign="left">
                <Typography variant="h4" color="textSecondary" gutterBottom>
                  Unavailable ({aggregatedUnavailable.length})
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  <Link sx={{ cursor: "pointer" }} onClick={() => setShowUnavailableList((prev) => !prev)}>Click here</Link> to view items unavailable for {moment(date).format("ddd, MMM D, YYYY")}.
                </Typography>
              </Box>
              
              {showUnavailableList && (
                <Grid container spacing={3} sx={{ marginTop: 1 }}>
                  {aggregatedUnavailable.map((product, index) => (
                    <Grid item xs={12} sm={6} key={`unavailable-${index}`}>
                      <ProductSummary product={product} query={query} date={date} setDate={setDate} isAvailable={false} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
        </Box>
      </Container>
    );
  }
}

